import React, { useEffect, useRef, useState } from 'react';

import { Person } from './workQueueLists';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface PeoplePickerProps {
  value: Person | null;
  onChange: (person: Person | null) => void;
  disabled?: boolean;
}

/**
 * Picks one person from the directory via the signed-in user's Graph search.
 * Falls back to typing an address when directory search is unavailable.
 */
const PeoplePicker: React.FC<PeoplePickerProps> = ({ value, onChange, disabled }) => {
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Person[]>([]);
  const [searching, setSearching] = useState(false);
  const [manualOnly, setManualOnly] = useState(false);
  const [manualError, setManualError] = useState('');
  const [searchedFor, setSearchedFor] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const search = (text: string) => {
    setQuery(text);
    setManualError('');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (manualOnly || text.trim().length < 2) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await axios.get<Person[]>('/api/settings/people/search', {
          params: { q: text.trim() },
          withCredentials: true
        });
        setResults(response.data);
        setSearchedFor(text);
      } catch (error: any) {
        if (error?.response?.status === 503) setManualOnly(true);
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  const choose = (person: Person) => {
    onChange(person);
    setQuery('');
    setResults([]);
  };

  const chooseTyped = () => {
    const upn = query.trim().toLowerCase();
    if (!EMAIL_PATTERN.test(upn)) {
      setManualError('Enter a full email address.');
      return;
    }
    choose({ upn, displayName: upn });
  };

  if (value) {
    return (
      <div className="wq-person-chip" title={value.upn}>
        <span>{value.displayName}</span>
        <button
          type="button"
          aria-label={`Unassign ${value.displayName}`}
          onClick={() => onChange(null)}
          disabled={disabled}
        >
          ×
        </button>
      </div>
    );
  }

  return (
    <div className="wq-people-picker">
      <div className="wq-people-row">
        <input
          type="text"
          value={query}
          onChange={(event) => search(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && manualOnly) {
              event.preventDefault();
              chooseTyped();
            }
          }}
          placeholder={manualOnly ? 'Email address' : 'Assign to… (name or email)'}
          aria-label="Assigned to"
          autoComplete="off"
          disabled={disabled}
        />
        {manualOnly && (
          <button type="button" className="btn btn-sm btn-secondary" onClick={chooseTyped} disabled={disabled}>
            Set
          </button>
        )}
        {user?.email && (
          <button
            type="button"
            className="link-button"
            onClick={() => choose({ upn: user.email.toLowerCase(), displayName: user.name || user.email })}
            disabled={disabled}
          >
            Me
          </button>
        )}
      </div>
      {searching && <div className="wq-people-hint">Searching…</div>}
      {manualError && <div className="wq-people-hint wq-people-error">{manualError}</div>}
      {manualOnly && !manualError && (
        <div className="wq-people-hint">Directory search is unavailable; type an address.</div>
      )}
      {results.length > 0 && (
        <ul className="wq-people-results">
          {results.map((person) => (
            <li key={person.upn}>
              <button type="button" onClick={() => choose(person)}>
                <strong>{person.displayName}</strong>
                <span>{person.upn}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {!searching && !manualOnly && searchedFor === query && query.trim().length >= 2 && results.length === 0 && (
        <div className="wq-people-hint">No matches.</div>
      )}
    </div>
  );
};

export default PeoplePicker;
